import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import {
  PC_HEIGHT_OFFSET,
  selectPanoCameraTransform,
} from "@/utils/camera-transform";
import {
  IElementGenericImgSheet,
  IElementImg360,
} from "@faro-lotv/ielement-types";
import { memberWithPrivateData } from "@faro-lotv/lotv";
import { IElementsState } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import {
  Camera,
  Matrix4,
  Object3D,
  Quaternion,
  Vector3,
  Vector3Tuple,
} from "three";

/**
 *
 * @param panoPositions List of positions of panos in the scene
 * @param sheetAltitude Altitude of reference sheet
 * @param cameraX Camera position X coordinate
 * @param cameraY Camera position Y coordinate
 * @param cameraZ Camera position Z coordinate
 * @returns The target camera position of the to-3d-model walk animation
 */
export function compute3DWalkAnimationTargetPosition(
  panoPositions: Vector3Tuple[],
  sheetAltitude: number,
  cameraX: number,
  cameraY: number,
  cameraZ: number,
): Vector3 {
  const cpArray: Vector3Tuple = [cameraX, cameraY, cameraZ];
  const closestPano = panoPositions.reduce<Vector3Tuple | undefined>(
    (closest, current) => {
      if (
        !closest ||
        squaredDistance(current, cpArray) < squaredDistance(closest, cpArray)
      ) {
        return current;
      }
      return closest;
    },
    undefined,
  );
  if (closestPano) {
    return new Vector3(cpArray[0], closestPano[1], cpArray[2]);
  }
  return new Vector3(cpArray[0], sheetAltitude + PC_HEIGHT_OFFSET, cpArray[2]);
}

/**
 * @param state The current state of the app store
 * @param panos The current panoramic images in the scene
 * @param sheet The current reference sheet for navigation
 * @returns The positions of the panoramic images in global coordinates
 */
export const selectPanoPosition = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, panos: IElementImg360[]) => panos,
      (
        state: OnlyIElementsStore,
        panos: IElementImg360[],
        sheet: IElementGenericImgSheet | undefined,
      ) => sheet,
    ],
    computePanoPositions,
  ),
);

function computePanoPositions(
  iElements: IElementsState,
  panos: IElementImg360[],
  sheet: IElementGenericImgSheet | undefined,
): Vector3Tuple[] {
  const state = { iElements };
  return panos.map(
    (pano) => selectPanoCameraTransform(pano, sheet)(state).position,
  );
}

/** @returns  the squared distance between two point */
const squaredDistance = memberWithPrivateData(() => {
  const A2D = new Vector3();
  const B2D = new Vector3();
  return (a: Vector3Tuple, b: Vector3Tuple): number =>
    A2D.fromArray(a).distanceToSquared(B2D.fromArray(b));
});

const EPSILON_SQ = 1e-6;

/**
 * This function takes as input a camera, projects its focal axis on the horizontal
 * plane, and returns the quaternion that expresses the new rotation of the camera.
 *
 * @param camera Current camera
 * @returns A quaternion expressing the rotation that the camera has only around the vertical axis
 */
export function toHorizontalFocalAxis(camera: Camera): Quaternion {
  const dir = camera.getWorldDirection(new Vector3());
  dir.y = 0;
  if (dir.lengthSq() < EPSILON_SQ) dir.x = 1;
  const mat = new Matrix4().lookAt(new Vector3(), dir, Object3D.DEFAULT_UP);
  return new Quaternion().setFromRotationMatrix(mat);
}
